import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Link, Typography } from "@mui/material";
import { EConsentResponse } from "../../services/ApiService";
import { useApiService } from "../../services/ApiServiceProvider";
import { useKeycloak } from "../../services/KeycloakProvider";

export const ConsentPage = () => {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const consentStatus = queryParams.get('status');
  const { t } = useTranslation();
  const apiService = useApiService();
  const { userInfo } = useKeycloak();
  const [eConsent, setEConsent] = useState<EConsentResponse | null>(null);
  const [loadingConsent, setLoadingConsent] = useState(false);

  useEffect(() => {
    const participantId = userInfo?.subjectId;
    if (!participantId || consentStatus !== 'Pending') return;

    setLoadingConsent(true);
    apiService.getEconsentSurveyLinks(participantId)
      .then(setEConsent)
      .catch(console.error)
      .then(() => setLoadingConsent(false), () => setLoadingConsent(false));
  }, [userInfo?.subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const eConsentAvailable = eConsent?.enabled && eConsent.surveyLinks?.length > 0;
  const allCompleted = eConsentAvailable ? eConsent!.surveyLinks.every(s => s.completed) : false;

  return (
    <div>
      <Typography variant="h4" component="h1" id="page-heading" tabIndex={-1}>{t('Consent Required')}</Typography>

      <Typography variant="body1" style={{ marginTop: '1rem' }}>
        {t('Your current consent status is:')} <strong>{t(consentStatus || 'Unknown')}</strong>
      </Typography>

      {consentStatus === 'Pending' && (
        <>
          {loadingConsent && <CircularProgress style={{ marginTop: '1rem' }} />}

          {!loadingConsent && eConsent !== null && !eConsentAvailable && (
            <Typography variant="body1" style={{ marginTop: '1rem' }}>
              {eConsent.site
                ? t('The institution {{site}} does not support eConsent. Please contact your site coordinator to complete the consent process.', { site: eConsent.site })
                : t('eConsent is not available for your institution. Please contact your site coordinator to complete the consent process.')
              }
            </Typography>
          )}

          {!loadingConsent && eConsentAvailable && !allCompleted && (
            <Box style={{ marginTop: '1.5rem' }}>
              <Typography variant="body1">
                {t('Please complete the following consent survey(s) to proceed:')}
              </Typography>
              {eConsent!.surveyLinks.map((survey, index) => (
                <Box key={index} style={{ marginTop: '1.5rem' }}>
                  <Box display="flex" alignItems="center" gap={1} style={{ marginBottom: '0.5rem' }}>
                    <Typography variant="body2">
                      {t('Survey')} {index + 1}:{' '}
                      <Link href={survey.url} target="_blank" rel="noopener noreferrer">
                        {t('Open in new tab')}
                      </Link>
                    </Typography>
                    {survey.completed && (
                      <Chip label={t('Completed')} color="success" size="small" />
                    )}
                  </Box>
                  {!survey.completed && (
                    <iframe
                      src={survey.url}
                      title={`${t('Consent Survey')} ${index + 1}`}
                      style={{ width: '100%', height: '600px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {!loadingConsent && allCompleted && (
            <Typography variant="body1" color="success.main" style={{ marginTop: '1.5rem' }}>
              {t('You have completed all consent surveys. Your submission is under review — someone will contact you once your consent status has been updated.')}
            </Typography>
          )}
        </>
      )}

      {consentStatus === 'Withdrawn Consent' && (
        <Typography variant="body1" style={{ marginTop: '1rem' }}>
          {t('Your consent has been withdrawn. Please contact your site coordinator if you wish to re-enroll.')}
        </Typography>
      )}

      <Typography variant="body1" style={{ marginTop: '1.5rem' }}>
        {t('If you have recently completed your consent, please return to home to check your updated status.')}
      </Typography>

      <Button variant="contained" style={{ marginTop: '1rem' }} onClick={() => navigate('/home')}>{t('Return to Home')}</Button>
    </div>
  );
};
